import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { STORAGE_KEYS } from '../utils/constants';
import Header from './Header';
import PrivacySecurityNotice from './PrivacySecurityNotice';

const Sidebar = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const { user, logout, hasModuleAccess } = useAuth();
  const { logoUrl, logoAnimation, ambulanceNumber } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoError, setLogoError] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [favouriteMenus, setFavouriteMenus] = useState([]);

  // Reset logo error when logoUrl changes
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  // Load favourites from localStorage + sync via custom event
  useEffect(() => {
    const loadFavourites = () => {
      const stored = localStorage.getItem(`favouriteMenus_${user?.role}`);
      if (stored) {
        try {
          setFavouriteMenus(JSON.parse(stored));
        } catch {
          setFavouriteMenus([]);
        }
      } else {
        setFavouriteMenus([]);
      }
    };
    loadFavourites();
    window.addEventListener('favouritesChanged', loadFavourites);
    return () => window.removeEventListener('favouritesChanged', loadFavourites);
  }, [user?.role]);

  const toggleFavourite = (path, e) => {
    e.stopPropagation();
    const updated = favouriteMenus.includes(path)
      ? favouriteMenus.filter((p) => p !== path)
      : [...favouriteMenus, path];
    setFavouriteMenus(updated);
    localStorage.setItem(`favouriteMenus_${user?.role}`, JSON.stringify(updated));
    window.dispatchEvent(new Event('favouritesChanged'));
  };

  const isFavourite = (path) => favouriteMenus.includes(path);

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  // Close mobile profile popup on route change
  useEffect(() => {
    setMobileProfileOpen(false);
  }, [location.pathname]);

  // Auto-open group if a child is active
  useEffect(() => {
    const items = getMenuItems();
    items.forEach((item) => {
      if (item.group && item.children) {
        const hasActiveChild = item.children.some((child) => location.pathname === child.path);
        if (hasActiveChild) {
          setOpenGroups((prev) => ({ ...prev, [item.group]: true }));
        }
      }
    });
  }, [location.pathname, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Session time tracking
  useEffect(() => {
    if (!user) return;

    const updateSessionTime = () => {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) return;

      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const expiryTime = tokenPayload.exp * 1000;
        const currentTime = Date.now();
        const timeLeft = expiryTime - currentTime;

        if (timeLeft > 0) {
          const hours = Math.floor(timeLeft / (1000 * 60 * 60));
          const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
          setSessionTimeLeft(`${hours}h ${minutes}m`);
        } else {
          setSessionTimeLeft('Expired');
        }
      } catch (error) {
        console.error('Session time calculation error:', error);
        setSessionTimeLeft('Invalid');
      }
    };

    updateSessionTime();
    const interval = setInterval(updateSessionTime, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const getMenuItems = () => {
    const basePath = `/${user?.role?.toLowerCase()}`;

    const allMenuItems = {
      superadmin: [
        { path: `${basePath}/dashboard`, icon: 'bi-speedometer2', label: 'Dashboard' },
        { path: `${basePath}/user-approvals`, icon: 'bi-people-fill', label: 'User Management' },
        { path: `${basePath}/user-directory`, icon: 'bi-person-lines-fill', label: 'User Directory' },
        { path: `${basePath}/subscription-plans`, icon: 'bi-box', label: 'Subscription Plans' },
        { path: `${basePath}/subscriptions`, icon: 'bi-credit-card', label: 'Subscriptions' },
        { path: `${basePath}/coupons`, icon: 'bi-tag', label: 'Coupons' },
        { path: `${basePath}/settings`, icon: 'bi-gear', label: 'Settings' },
      ],
      admin: [
        { path: `${basePath}/home`, icon: 'bi-house-door', label: 'Home' },
        { path: `${basePath}/dashboard`, icon: 'bi-speedometer2', label: 'Dashboard' },
        { path: `${basePath}/patient-search`, icon: 'bi-search', label: 'Patient Search' },
        { path: `${basePath}/book-appointment`, icon: 'bi-calendar-plus', label: 'Book Appointment' },
        { path: `${basePath}/appointments`, icon: 'bi-calendar-check', label: 'Appointments' },
        { path: `${basePath}/availability-management`, icon: 'bi-clock-history', label: 'Dr. Availability Manager' },
        {
          group: 'User Management',
          icon: 'bi-people',
          children: [
            { path: `${basePath}/doctors`, icon: 'bi-person-badge', label: 'Doctors' },
            { path: `${basePath}/staff`, icon: 'bi-headset', label: 'Support' },
            { path: `${basePath}/patients`, icon: 'bi-people', label: 'Patients' },
          ],
        },
        { path: `${basePath}/specializations`, icon: 'bi-hospital', label: 'Specializations' },
        { path: `${basePath}/tests`, icon: 'bi-clipboard-pulse', label: 'Tests & Checkups' },
        { path: `${basePath}/services`, icon: 'bi-gear', label: 'Services' },
        { path: `${basePath}/treatments`, icon: 'bi-bandaid', label: 'Treatments' },
        { path: `${basePath}/diagnostics`, icon: 'bi-clipboard2-pulse', label: 'Diagnostics' },
        { path: `${basePath}/gallery`, icon: 'bi-images', label: 'Gallery' },
        { path: `${basePath}/testimonials`, icon: 'bi-chat-quote', label: 'Testimonials' },
        { path: `${basePath}/reports`, icon: 'bi-bar-chart', label: 'Reports' },
        { path: `${basePath}/collection-report`, icon: 'bi-receipt-cutoff', label: 'Collection Report' },
        { path: `${basePath}/checked-patients`, icon: 'bi-clipboard2-check', label: 'Checked Patients' },
        { path: `${basePath}/subscription`, icon: 'bi-credit-card', label: 'Subscription' },
        { path: `${basePath}/business-settings`, icon: 'bi-gear', label: 'Settings' },
      ],
      doctor: [
        { path: `${basePath}/home`, icon: 'bi-house-door', label: 'Home' },
        { path: `${basePath}/dashboard`, icon: 'bi-speedometer2', label: 'Dashboard' },
        { path: `${basePath}/patient-search`, icon: 'bi-search', label: 'Patient Search' },
        { path: `${basePath}/book-appointment`, icon: 'bi-calendar-plus', label: 'Book Appointment' },
        { path: `${basePath}/appointments`, icon: 'bi-calendar-check', label: 'Appointments' },
        { path: `${basePath}/availability-management`, icon: 'bi-clock-history', label: 'Availability' },
        { path: `${basePath}/doctor-holidays`, icon: 'bi-calendar-x', label: 'Doctor Holidays' },
        { path: `${basePath}/patients`, icon: 'bi-people', label: 'Patients' },
        { path: `${basePath}/medicines`, icon: 'bi-capsule', label: 'Medicines' },
        { path: `${basePath}/tests`, icon: 'bi-clipboard-pulse', label: 'Tests & Checkups' },
        { path: `${basePath}/reports`, icon: 'bi-bar-chart', label: 'Report Dashboard' },
        { path: `${basePath}/collection-report`, icon: 'bi-receipt-cutoff', label: 'Collection Report' },
        { path: `${basePath}/checked-patients`, icon: 'bi-clipboard2-check', label: 'Checked Patients' },
        { path: `${basePath}/settings`, icon: 'bi-gear', label: 'Settings' },
      ],
      staff: [
        { path: `${basePath}/home`, icon: 'bi-house-door', label: 'Home' },
        { path: `${basePath}/dashboard`, icon: 'bi-speedometer2', label: 'Dashboard', module: 'dashboard' },
        { path: `${basePath}/patient-search`, icon: 'bi-search', label: 'Patient Search', module: 'patient-search' },
        {
          path: `${basePath}/book-appointment`,
          icon: 'bi-calendar-plus',
          label: 'Book Appointment',
          module: 'appointments',
        },
        { path: `${basePath}/appointments`, icon: 'bi-calendar-check', label: 'Appointments', module: 'appointments' },
        {
          path: `${basePath}/availability-management`,
          icon: 'bi-clock-history',
          label: 'Dr. Availability Manager',
          module: 'availability',
        },
        {
          group: 'User Management',
          icon: 'bi-people',
          children: [
            { path: `${basePath}/staff`, icon: 'bi-headset', label: 'Support', module: 'staff' },
            {
              path: `${basePath}/patient-users`,
              icon: 'bi-person-plus',
              label: 'Patient Users',
              module: 'patient-users',
            },
          ].filter((child) => hasModuleAccess(child.module)),
        },
        { path: `${basePath}/doctors`, icon: 'bi-person-badge', label: 'Doctors', module: 'doctors' },
        { path: `${basePath}/patients`, icon: 'bi-people', label: 'Patients', module: 'patients' },
        {
          path: `${basePath}/specializations`,
          icon: 'bi-hospital',
          label: 'Specializations',
          module: 'specializations',
        },
        { path: `${basePath}/tests`, icon: 'bi-clipboard-pulse', label: 'Tests & Checkups', module: 'tests' },
        { path: `${basePath}/services`, icon: 'bi-gear-wide-connected', label: 'Services', module: 'services' },
        { path: `${basePath}/billing`, icon: 'bi-receipt', label: 'Billing', module: 'billing' },
        { path: `${basePath}/payments`, icon: 'bi-credit-card', label: 'Payments', module: 'payments' },
        { path: `${basePath}/reports`, icon: 'bi-bar-chart', label: 'Report Dashboard', module: 'reports' },
        {
          path: `${basePath}/collection-report`,
          icon: 'bi-receipt-cutoff',
          label: 'Collection Report',
          module: 'reports',
        },
        {
          path: `${basePath}/checked-patients`,
          icon: 'bi-clipboard2-check',
          label: 'Checked Patients',
          module: 'reports',
        },
        {
          path: `${basePath}/business-settings`,
          icon: 'bi-shop',
          label: 'Business Settings',
          module: 'business-settings',
        },
      ].filter((item) => {
        // Groups: show if they have children after filtering
        if (item.group && item.children) return item.children.length > 0;
        // Regular items: check module access
        return hasModuleAccess(item.module);
      }),
      patient: [
        { path: `${basePath}/home`, icon: 'bi-house-door', label: 'Home' },
        { path: `${basePath}/dashboard`, icon: 'bi-speedometer2', label: 'Dashboard' },
        { path: `${basePath}/book-appointment`, icon: 'bi-calendar-plus', label: 'Book Appointment' },
        { path: `${basePath}/appointments`, icon: 'bi-calendar-check', label: 'My Appointments' },
        { path: `${basePath}/doctors`, icon: 'bi-person-badge', label: 'Doctors' },
        { path: `${basePath}/prescriptions`, icon: 'bi-file-earmark-medical', label: 'Prescriptions' },
        { path: `${basePath}/bills`, icon: 'bi-receipt', label: 'Bills & Payments' },
        { path: `${basePath}/medical-records`, icon: 'bi-journal-medical', label: 'Medical Records' },
        { path: `${basePath}/family-members`, icon: 'bi-people', label: 'Family Members' },
        { path: `${basePath}/emergency-contacts`, icon: 'bi-telephone-plus', label: 'Emergency Contacts' },
        { path: `${basePath}/profile`, icon: 'bi-person-circle', label: 'My Profile' },
        { path: `${basePath}/settings`, icon: 'bi-gear', label: 'Settings' },
      ],
    };

    return allMenuItems[user?.role] || [];
  };

  const menuItems = getMenuItems();

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => location.pathname === path;

  const getUserInitials = () => {
    const name = user?.full_name || user?.name || 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <div className="d-flex min-vh-100">
        {/* ── SIDEBAR ── */}
        <div
          className={`sidebar ce-sidebar d-flex flex-column ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        >
          {/* Logo + Mobile Close */}
          <div className="ce-logo-section">
            <div
              className={`ce-logo-wrap ${logoAnimation === 'shine-gloss' ? 'logo-wrap-shine' : ''}`}
              onClick={() => {
                const homePaths = { admin: '/admin/home', staff: '/staff/home' };
                navigate(homePaths[user?.role] || `/${user?.role}/dashboard`);
                setSidebarOpen(false);
              }}
              role="button"
              style={{ cursor: 'pointer' }}
            >
              {logoUrl && !logoError ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className={`ce-logo-img logo-anim-${logoAnimation || 'none'}`}
                  onError={() => setLogoError(true)}
                />
              ) : (
                <img
                  src={sidebarCollapsed ? '/hqms_favicon.png' : '/hqms_logo.png'}
                  alt="Logo"
                  className={`ce-logo-img logo-anim-${logoAnimation || 'none'}`}
                />
              )}
            </div>
            <button
              className="ce-sidebar-close d-lg-none"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* Navigation */}
          <nav className="ce-nav-scroll">
            {/* Favourites Section */}
            {!sidebarCollapsed &&
              (() => {
                const allFlat = menuItems.flatMap((item) => (item.group && item.children ? item.children : [item]));
                const favItems = allFlat.filter((item) => isFavourite(item.path));
                if (favItems.length === 0) return null;
                return (
                  <div className="ce-favourites">
                    <div className="ce-section-label">
                      <i className="bi bi-star-fill me-1" style={{ fontSize: '0.55rem' }}></i>
                      Favourites
                    </div>
                    <ul className="nav flex-column">
                      {favItems.map((item) => (
                        <li className="nav-item" key={`fav-${item.path}`}>
                          <div
                            className={`ce-menu-item ${isActive(item.path) ? 'active' : ''}`}
                            data-tooltip={item.label}
                          >
                            <button
                              className="ce-menu-btn"
                              onClick={() => {
                                navigate(item.path);
                                setSidebarOpen(false);
                              }}
                            >
                              <span className="ce-icon-wrap">
                                <i className={item.icon}></i>
                              </span>
                              <span className="sidebar-label">{item.label}</span>
                            </button>
                            <button
                              className="ce-star-btn starred"
                              onClick={(e) => toggleFavourite(item.path, e)}
                              title="Remove from favourites"
                            >
                              <i className="bi bi-star-fill"></i>
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

            {/* Menu Label */}
            {!sidebarCollapsed && <div className="ce-section-label">Menu</div>}

            {/* Menu Items */}
            <ul className="nav flex-column">
              {menuItems.map((item, index) => {
                // Group menu
                if (item.group && item.children) {
                  const isGroupOpen = openGroups[item.group];
                  const hasActiveChild = item.children.some((child) => isActive(child.path));
                  return (
                    <li className="nav-item" key={`group-${item.group}`}>
                      <button
                        className={`ce-group-header ${hasActiveChild ? 'has-active' : ''}`}
                        onClick={() => toggleGroup(item.group)}
                        data-tooltip={sidebarCollapsed ? item.group : undefined}
                      >
                        <span className="ce-icon-wrap">
                          <i className={item.icon}></i>
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="sidebar-label">{item.group}</span>
                            <i className={`bi bi-chevron-right ce-group-chevron ${isGroupOpen ? 'open' : ''}`}></i>
                          </>
                        )}
                      </button>
                      <div className={`ce-group-children ${isGroupOpen && !sidebarCollapsed ? 'open' : ''}`}>
                        {item.children.map((child) => (
                          <div key={child.path}>
                            <div
                              className={`ce-menu-item ce-child-item ${isActive(child.path) ? 'active' : ''}`}
                              data-tooltip={child.label}
                            >
                              <button
                                className="ce-menu-btn"
                                onClick={() => {
                                  navigate(child.path);
                                  setSidebarOpen(false);
                                }}
                              >
                                <span className="ce-icon-wrap">
                                  <i className={child.icon}></i>
                                </span>
                                <span className="sidebar-label">{child.label}</span>
                              </button>
                              {!sidebarCollapsed && (
                                <button
                                  className={`ce-star-btn ${isFavourite(child.path) ? 'starred' : ''}`}
                                  onClick={(e) => toggleFavourite(child.path, e)}
                                  title={isFavourite(child.path) ? 'Remove from favourites' : 'Add to favourites'}
                                >
                                  <i className={`bi ${isFavourite(child.path) ? 'bi-star-fill' : 'bi-star'}`}></i>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </li>
                  );
                }

                // Regular menu item
                return (
                  <li className="nav-item" key={item.path}>
                    <div className={`ce-menu-item ${isActive(item.path) ? 'active' : ''}`} data-tooltip={item.label}>
                      <button
                        className="ce-menu-btn"
                        onClick={() => {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }}
                      >
                        <span className="ce-icon-wrap">
                          <i className={item.icon}></i>
                        </span>
                        <span className={`sidebar-label ${sidebarCollapsed ? 'd-none' : ''}`}>{item.label}</span>
                      </button>
                      {!sidebarCollapsed && (
                        <button
                          className={`ce-star-btn ${isFavourite(item.path) ? 'starred' : ''}`}
                          onClick={(e) => toggleFavourite(item.path, e)}
                          title={isFavourite(item.path) ? 'Remove from favourites' : 'Add to favourites'}
                        >
                          <i className={`bi ${isFavourite(item.path) ? 'bi-star-fill' : 'bi-star'}`}></i>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Managed by badge */}
          <a href="https://webdekho.in" target="_blank" rel="noopener noreferrer" className="managed-by-sidebar">
            <img src="https://webdekho.in/images/wpl_qms.png" alt="Webdekho" className="managed-by-logo" />
          </a>

          {/* Sidebar Footer */}
          <div className="ce-signout-wrapper">
            <button className="ce-signout-btn" onClick={handleLogout} title="Sign Out">
              <i className="bi bi-box-arrow-right"></i>
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className={`flex-grow-1 d-flex flex-column main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          {/* Privacy & Security Notice */}
          <PrivacySecurityNotice />
          {/* Header */}
          <Header
            onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
            onCollapseSidebar={() => setSidebarCollapsed((prev) => !prev)}
            sidebarCollapsed={sidebarCollapsed}
          />
          {/* Main Content Area */}
          <main className="flex-grow-1 p-4 main-content-body ce-mobile-bottom-pad">{children}</main>
        </div>

        {/* ── BOTTOM NAVIGATION (Mobile Only) ── */}
        {!sidebarOpen && (
          <nav className="ce-bottom-nav d-lg-none">
            {/* Left items */}
            {[
              { path: `/${user?.role}/home`, icon: 'bi-house-door', activeIcon: 'bi-house-door-fill', label: 'Home' },
              {
                path: `/${user?.role}/book-appointment`,
                icon: 'bi-calendar-plus',
                activeIcon: 'bi-calendar-plus-fill',
                label: 'Book',
              },
            ].map((item) => {
              const active = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  className={`ce-bottom-nav-item ${active ? 'active' : ''}`}
                  onClick={() => navigate(item.path)}
                >
                  <i className={`bi ${active ? item.activeIcon : item.icon}`}></i>
                  <span>{item.label}</span>
                </button>
              );
            })}

            {/* Center FAB - Appointments */}
            <div className="ce-bottom-nav-center">
              <button
                className={`ce-fab-btn ${location.pathname === `/${user?.role}/appointments` ? 'active' : ''}`}
                onClick={() => navigate(`/${user?.role}/appointments`)}
              >
                <i className="bi bi-calendar-check-fill"></i>
              </button>
              <span className="ce-fab-label">Appointments</span>
            </div>

            {/* Profile */}
            {(() => {
              const profilePaths = {
                admin: `/${user?.role}/business-settings`,
                doctor: `/${user?.role}/settings`,
                staff: `/${user?.role}/settings`,
                superadmin: `/${user?.role}/settings`,
                patient: `/${user?.role}/profile`,
              };
              const profilePath = profilePaths[user?.role] || `/${user?.role}/settings`;
              const isProfileActive = location.pathname === profilePath;
              return (
                <div className="ce-bottom-nav-profile-wrap">
                  {mobileProfileOpen && (
                    <div className="ce-mobile-profile-popup">
                      <button
                        className="ce-mobile-profile-popup-item"
                        onClick={() => {
                          navigate(profilePath);
                          setMobileProfileOpen(false);
                        }}
                      >
                        <i className="bi bi-gear"></i>
                        <span>Settings</span>
                      </button>
                      <button
                        className="ce-mobile-profile-popup-item ce-mobile-logout-item"
                        onClick={() => {
                          setMobileProfileOpen(false);
                          handleLogout();
                        }}
                      >
                        <i className="bi bi-box-arrow-right"></i>
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )}
                  <button
                    className={`ce-bottom-nav-item ${isProfileActive || mobileProfileOpen ? 'active' : ''}`}
                    onClick={() => setMobileProfileOpen((prev) => !prev)}
                  >
                    <i className={`bi ${isProfileActive ? 'bi-person-fill' : 'bi-person-circle'}`}></i>
                    <span>Profile</span>
                  </button>
                </div>
              );
            })()}

            {/* More */}
            <button
              className="ce-bottom-nav-item"
              onClick={() => {
                setMobileProfileOpen(false);
                setSidebarOpen(true);
              }}
            >
              <i className="bi bi-grid"></i>
              <span>More</span>
            </button>
          </nav>
        )}

        {/* Mobile Profile Popup Overlay */}
        {mobileProfileOpen && (
          <div className="ce-mobile-profile-overlay d-lg-none" onClick={() => setMobileProfileOpen(false)}></div>
        )}

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && <div className="ce-overlay d-lg-none" onClick={() => setSidebarOpen(false)}></div>}
      </div>
    </>
  );
};

export default Sidebar;
